import React, { useEffect, useMemo, useState } from "react";
import { Table, Avatar, Typography, Tag, Tooltip, Space, theme } from "antd";
import {
  PlusOutlined,
  EyeInvisibleOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Employee, ScheduleType, Day } from "../../../types/schedule";
import { getOptimalTextColor } from "../../../Util/colorUtils";
import ScheduleDetailsDrawer from "./ScheduleDetailsDrawer";

const { Text } = Typography;

interface WeekViewGridProps {
  employees: Employee[];
  scheduleData: ScheduleType[];
  currentWeek: Date;
  onAddEvent: (event: any) => void;
  onRefresh?: () => void;
  loading?: boolean;
}

// Your custom category colors
const categoryColors: any = {
  scheduled: "green",
  timeoffpending: "orange",
  timeoff: "red",
  absent: "volcano",
  meeting: "blue",
  other: "default",
};

const WeekViewGrid: React.FC<WeekViewGridProps> = ({
  employees,
  scheduleData,
  currentWeek,
  onAddEvent,
  onRefresh,
  loading = false,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(
    null,
  );
  const [hoveredSlot, setHoveredSlot] = useState<string | null>(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const { token } = theme.useToken();

  // Monitor screen width for responsive behavior
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate responsive column width based on screen size
  const getColumnWidth = () => {
    if (screenWidth < 768) return 120; // Mobile
    if (screenWidth < 1024) return 130; // Tablet
    if (screenWidth < 1280) return 140; // Small desktop
    return 140; // Large desktop
  };

  // Calculate employee column width
  const getEmployeeColumnWidth = () => {
    if (screenWidth < 768) return 80; // Mobile
    if (screenWidth < 1024) return 120; // Tablet
    return 140; // Desktop
  };

  // Generate all 7 days of the week (Sunday to Saturday)
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    dayjs(currentWeek).add(i, "day").toDate(),
  );

  const parseTimeToMinutes = (timeValue: string) => {
    const match = timeValue.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const period = match[3].toUpperCase();
    const normalizedHours = (hours % 12) + (period === "PM" ? 12 : 0);
    return normalizedHours * 60 + minutes;
  };

  const getDurationMinutes = (timeRange: string) => {
    const parts = timeRange.split(" - ");
    if (parts.length !== 2) return 0;
    const start = parseTimeToMinutes(parts[0]);
    const end = parseTimeToMinutes(parts[1]);
    if (start === null || end === null) return 0;
    const adjustedEnd = end < start ? end + 24 * 60 : end;
    return Math.max(0, adjustedEnd - start);
  };

  const totalHoursByEmployee = useMemo(() => {
    const totals = new Map<string, number>();
    scheduleData.forEach((schedule) => {
      const totalMinutes = schedule.days
        .flat()
        .reduce((sum, day) => sum + getDurationMinutes(day.time), 0);
      totals.set(schedule.key.toString(), totalMinutes / 60);
    });
    return totals;
  }, [scheduleData]);

  const handleEmptySlotClick = (employee: Employee, date: Date) => {
    // Convert to your event format for the add modal
    const eventData = {
      employeeId: employee.id,
      title: "New Event",
      description: "New schedule event",
      startTime: "09:00",
      endTime: "17:00",
      category: "scheduled",
      date: dayjs(date).format("YYYY-MM-DD"),
    };
    onAddEvent(eventData);
  };

  const handleEventClick = (event: Day, employee: Employee) => {
    const resolvedScheduleId =
      typeof event.scheduleId === "number"
        ? event.scheduleId
        : Number(event.key);

    if (!Number.isFinite(resolvedScheduleId)) {
      return;
    }

    setSelectedScheduleId(resolvedScheduleId);
    setIsDrawerOpen(true);
  };

  // Get schedule data for a specific employee and day
  const getEmployeeScheduleForDay = (
    employeeId: string,
    dayIndex: number,
  ): Day[] => {
    const employeeSchedule = scheduleData.find(
      (schedule) => schedule.key.toString() === employeeId,
    );
    return employeeSchedule?.days[dayIndex] || [];
  };

  const columns = [
    {
      title: "Employee",
      dataIndex: "employee",
      key: "employee",
      fixed: "left" as const,
      width: getEmployeeColumnWidth(),
      render: (employee: Employee) => {
        //const location = getEmployeeLocation(employee.id);
        //const location = "BOBS SHOP";
        const totalHours = totalHoursByEmployee.get(employee.id) ?? 0;
        return (
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Space>
              <Avatar src={employee.avatar}>
                {employee.firstName?.[0] || employee.name[0]}
              </Avatar>
              <div>
                <Text strong style={{ color: token.colorText }}>
                  {employee.name}
                </Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {employee.role}
                  </Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Total: {totalHours.toFixed(2)}h
                  </Text>
                </div>
              </div>
            </Space>

            {/* Location indicator only */}
            {/*{location && (
              <Tooltip title={`Location: ${location.name}`}>
                <Tag
                  icon={<EnvironmentOutlined />}
                  color={location.color}
                  style={{
                    fontSize: 10,
                    padding: "2px 6px",
                    margin: 0,
                    borderRadius: 4,
                  }}
                >
                  {location.name.split(" - ")[0]}
                </Tag>
              </Tooltip>
            )}*/}
          </Space>
        );
      },
    },
    ...weekDays.map((day, dayIndex) => {
      const isCurrentDay = dayjs(day).isSame(dayjs(), "day");
      const dateKey = dayjs(day).format("YYYY-MM-DD");
      const columnWidth = getColumnWidth();

      return {
        title: (
          <div
            style={{
              textAlign: "center",
              padding: "8px 0",
              backgroundColor: isCurrentDay
                ? token.colorPrimaryBg
                : "transparent",
            }}
          >
            <div
              style={{
                color: isCurrentDay ? token.colorPrimary : token.colorText,
                fontWeight: isCurrentDay ? 600 : "normal",
              }}
            >
              {dayjs(day).format("ddd, MMM D")}
            </div>
            {isCurrentDay && (
              <div
                style={{
                  width: 24,
                  height: 2,
                  backgroundColor: token.colorPrimary,
                  margin: "4px auto 0",
                }}
              />
            )}
          </div>
        ),
        dataIndex: dateKey,
        key: dateKey,
        width: columnWidth,
        render: (_: any, record: any) => {
          const dayEvents = getEmployeeScheduleForDay(
            record.employee.id,
            dayIndex,
          );
          const slotKey = `${record.employee.id}-${dateKey}`;
          const isHovered = hoveredSlot === slotKey;

          return (
            <div
              style={{
                minHeight: 120,
                padding: 8,
                position: "relative",
                cursor: dayEvents.length === 0 ? "pointer" : "default",
                backgroundColor: isCurrentDay
                  ? token.colorPrimaryBg
                  : "transparent",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={() => {
                if (dayEvents.length === 0) {
                  setHoveredSlot(slotKey);
                }
              }}
              onMouseLeave={() => setHoveredSlot(null)}
              onClick={() => {
                if (dayEvents.length === 0) {
                  handleEmptySlotClick(record.employee, day);
                }
              }}
            >
              {dayEvents.length === 0 ? (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: isHovered ? 1 : 0,
                    transition: "opacity 0.2s ease",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <PlusOutlined
                      style={{
                        fontSize: 32,
                        color: token.colorPrimary,
                        border: `2px dashed ${token.colorPrimary}`,
                        borderRadius: "50%",
                        padding: 16,
                        backgroundColor: token.colorPrimaryBg,
                      }}
                    />
                    <div
                      style={{
                        marginTop: 8,
                        fontSize: 12,
                        color: token.colorPrimary,
                        fontWeight: 500,
                      }}
                    >
                      Click to add new schedule
                    </div>
                  </div>
                </div>
              ) : (
                <Space direction="vertical" size={6} style={{ width: "100%" }}>
                  {dayEvents.map((event) => {
                    // Use primary color as card background
                    const isAbsent = event.isAbsent === true;
                    const backgroundColor = isAbsent
                      ? token.colorWarningBg
                      : event.color || token.colorFillSecondary;
                    const textColor = isAbsent
                      ? token.colorWarningText
                      : getOptimalTextColor(backgroundColor);
                    const isUnpublished = event.isPublished === false;

                    return (
                      <div
                        key={event.key}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventClick(event, record.employee);
                        }}
                        style={{
                          cursor: "pointer",
                          padding: "12px",
                          borderRadius: 8,
                          backgroundColor,
                          border: `2px solid ${
                            isAbsent
                              ? token.colorWarningBorder
                              : backgroundColor
                          }`,
                          borderStyle: isUnpublished ? "dashed" : "solid",
                          opacity: isUnpublished ? 0.8 : 1,
                          transition: "all 0.2s ease",
                          position: "relative",
                          minHeight: 80,
                          background: `linear-gradient(135deg, ${backgroundColor} 0%, ${backgroundColor}dd 100%)`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = `0 4px 12px ${token.colorPrimaryBg}`;
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = "none";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        {/* Unpublished indicator */}
                        {isUnpublished && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              marginBottom: 6,
                            }}
                          >
                            <Tooltip title="Schedule not published">
                              <div
                                style={{
                                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                                  borderRadius: "50%",
                                  padding: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                }}
                              >
                                <EyeInvisibleOutlined
                                  style={{
                                    fontSize: 12,
                                    color: token.colorError,
                                  }}
                                />
                              </div>
                            </Tooltip>
                          </div>
                        )}

                        {/* Category tag */}
                        <div style={{ marginBottom: 8 }}>
                          <Tag
                            color={
                              categoryColors[event.tag.toLowerCase()] ||
                              "default"
                            }
                            style={{
                              margin: 0,
                              fontSize: 11,
                              lineHeight: 1.2,
                              padding: "3px 8px",
                              border: "none",
                              borderRadius: 4,
                              fontWeight: 500,
                            }}
                          >
                            {event.tag}
                          </Tag>
                        </div>

                        {/* Time */}
                        <div
                          style={{
                            fontSize: 12,
                            color: textColor,
                            opacity: 0.9,
                            fontWeight: 500,
                          }}
                        >
                          {event.time}
                        </div>

                        {/* Unpublished text indicator */}
                        {isUnpublished && (
                          <div
                            style={{
                              fontSize: 10,
                              color: textColor,
                              opacity: 0.8,
                              marginTop: 4,
                              fontStyle: "italic",
                              fontWeight: 500,
                            }}
                          >
                            Draft Schedule
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Space>
              )}
            </div>
          );
        },
      };
    }),
  ];

  const dataSource = employees.map((employee) => ({
    key: employee.id,
    employee,
  }));

  // Find today's column index for continuous highlighting
  const todayColumnIndex = weekDays.findIndex((day) =>
    dayjs(day).isSame(dayjs(), "day"),
  );

  return (
    <>
      <style>
        {`
          /* Clean today column highlighting - background only */
          ${
            todayColumnIndex >= 0
              ? `
            .ant-table-thead > tr > th:nth-child(${todayColumnIndex + 2}),
            .ant-table-tbody > tr > td:nth-child(${todayColumnIndex + 2}) {
              background-color: ${token.colorPrimaryBg} !important;
            }
            
            .ant-table-thead > tr > th:nth-child(${todayColumnIndex + 2}) {
              background-color: ${token.colorPrimaryBg} !important;
            }
          `
              : ""
          }
        `}
      </style>

      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        scroll={{
          x:
            screenWidth < 768
              ? getEmployeeColumnWidth() + getColumnWidth() * 7
              : "max-content",
        }}
        bordered
        loading={loading}
        style={{ backgroundColor: token.colorBgContainer }}
        size={screenWidth < 768 ? "small" : "middle"}
      />

      {selectedScheduleId !== null && (
        <ScheduleDetailsDrawer
          scheduleId={selectedScheduleId}
          open={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setSelectedScheduleId(null);
          }}
          onRefresh={onRefresh}
        />
      )}
    </>
  );
};

export default WeekViewGrid;
